import {
  useCloseSlideOverviewOnOutsideClick,
  useNavigatorDismissal,
  usePresentationKeyboardListener,
} from './util';
import { Slide } from './types';
import { connect, dispatchAction } from '@ardoq/rxbeach';
import { slideNavigator$ } from './slideNavigator$';
import {
  CollapsedNavigatorSlot,
  NavigatorBar,
  ShowNavigatorButton,
} from './atoms';
import { SlideNavigatorControls } from './SlideNavigatorControls';
import {
  hasDescription,
  isDescriptionShown,
  toggleDescription,
} from './descriptionState';
import { ArdoqId } from '@ardoq/api-types';
import { t, type I18nLocale } from '@ardoq/i18n';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { combineLatest, map } from 'rxjs';
import { PRESENTATION_CHROME_CLASS } from 'presentation/chrome';
import { useAutoStartNavigatorTour } from 'productTours/presentationNavigator/useAutoStartNavigatorTour';
import { currentUser$ } from 'streams/currentUser/currentUser$';
import { currentUserSettled$ } from 'presentation/streams/currentUserSettled$';
import { hasSeenNavigatorTour } from './navigatorPreferences';
import { hasSeenTourInThisBrowser$ } from './hasSeenTourInThisBrowser$';
import { deprecatedViews$ } from 'views/deprecatedViews$';
import type { MetaInfo } from 'streams/views/mainContent/types';

type OwnProps = { slides: Slide[]; presentationName: string };

type SlideNavigatorComponentProps = OwnProps & {
  selectedSlideId: ArdoqId | null;
  isSlideOverviewOpen: boolean;
  isNavigatorDismissed: boolean;
  isCurrentUserSettled: boolean;
  hasSeenTour: boolean;
  hasDescription: boolean;
  isDescriptionShown: boolean;
  onToggleDescription: VoidFunction;
  deprecatedViews: MetaInfo[];
  i18nLocale: I18nLocale;
};

const SlideNavigatorComponentBase = ({
  slides,
  presentationName,
  selectedSlideId,
  isSlideOverviewOpen,
  isNavigatorDismissed,
  isCurrentUserSettled,
  hasSeenTour,
  hasDescription,
  isDescriptionShown,
  onToggleDescription,
  deprecatedViews,
  i18nLocale,
}: SlideNavigatorComponentProps) => {
  const canToggleDescription = hasDescription && !isNavigatorDismissed;
  useAutoStartNavigatorTour({
    canRunTour:
      selectedSlideId !== null && !isNavigatorDismissed && isCurrentUserSettled,
    hasSeenTour,
  });

  usePresentationKeyboardListener(
    canToggleDescription ? onToggleDescription : null
  );
  useCloseSlideOverviewOnOutsideClick(isSlideOverviewOpen);
  const { showNavigatorRef, hideNavigatorRef, hideNavigator, showNavigator } =
    useNavigatorDismissal(isNavigatorDismissed);

  if (isNavigatorDismissed) {
    return (
      <CollapsedNavigatorSlot className={PRESENTATION_CHROME_CLASS}>
        <ShowNavigatorButton
          ref={showNavigatorRef}
          onClick={showNavigator}
          label={t(
            i18nLocale,
            'presentationAdmin:viewer.slideNavigator.showNavigator',
            'Show slide navigation'
          )}
        />
      </CollapsedNavigatorSlot>
    );
  }

  return (
    <NavigatorBar
      className={PRESENTATION_CHROME_CLASS}
      role="region"
      aria-label={t(
        i18nLocale,
        'presentationAdmin:viewer.slideNavigator.slideNavigationAriaLabel',
        'Slide navigation'
      )}
    >
      <SlideNavigatorControls
        slides={slides}
        presentationName={presentationName}
        selectedSlideId={selectedSlideId}
        isSlideOverviewOpen={isSlideOverviewOpen}
        hasDescription={hasDescription}
        isDescriptionShown={isDescriptionShown}
        onToggleDescription={onToggleDescription}
        onHideNavigator={hideNavigator}
        hideNavigatorRef={hideNavigatorRef}
        deprecatedViews={deprecatedViews}
        i18nLocale={i18nLocale}
      />
    </NavigatorBar>
  );
};

const slideNavigatorViewModel$ = combineLatest([
  slideNavigator$,
  i18nLocale$,
  currentUser$,
  currentUserSettled$,
  hasSeenTourInThisBrowser$,
  deprecatedViews$,
]).pipe(
  map(
    ([
      state,
      i18nLocale,
      currentUser,
      isCurrentUserSettled,
      hasSeenTourInThisBrowser,
      deprecatedViews,
    ]) => ({
      selectedSlideId: state.selectedSlideId,
      isSlideOverviewOpen: state.isSlideOverviewOpen,
      isNavigatorDismissed: state.isNavigatorDismissed,
      isCurrentUserSettled,
      hasSeenTour: hasSeenNavigatorTour({
        currentUser,
        hasSeenTourInThisBrowser,
      }),
      hasDescription: hasDescription(state),
      isDescriptionShown: isDescriptionShown(state),
      onToggleDescription: () => dispatchAction(toggleDescription(state)),
      deprecatedViews,
      i18nLocale,
    })
  )
);

export const SlideNavigatorComponent = connect(
  SlideNavigatorComponentBase,
  slideNavigatorViewModel$
);
